import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Spinner } from '@/components/ui/Spinner'
import { Pagination } from '@/components/ui/Pagination'
import { EmptyState } from '@/components/ui/EmptyState'
import { Badge } from '@/components/ui/Badge'
import { Modal } from '@/components/ui/Modal'
import {
  useAssistanceRequests,
  useAssistanceStats,
  useUpdateAssistanceRequest,
} from '@/hooks/useAssistance'
import { useAdmins } from '@/hooks/useAdmins'
import { useDebounce } from '@/hooks/useDebounce'
import {
  Wrench,
  Search,
  Clock,
  CheckCircle2,
  CreditCard,
  X,
} from 'lucide-react'
import {
  ASSISTANCE_STATUS_CONFIG,
  ASSISTANCE_STATUS_LABELS,
  ASSISTANCE_CATEGORY_LABELS,
  PRIORITY_CONFIG,
  PRIORITY_LABELS,
} from '@/types/assistance'
import type {
  AssistanceStatus,
  AssistancePriority,
  AssistanceRequestWithDetails,
} from '@/types/assistance'
import { formatDistanceToNow } from 'date-fns'
import { fr } from 'date-fns/locale'
import toast from 'react-hot-toast'

const ITEMS_PER_PAGE = 20

function RequestDetailModal({
  request,
  isOpen,
  onClose,
}: {
  request: AssistanceRequestWithDetails
  isOpen: boolean
  onClose: () => void
}) {
  const [status, setStatus] = useState(request.status)
  const [isCovered, setIsCovered] = useState<boolean | null>(request.is_covered)
  const [estimatedHours, setEstimatedHours] = useState(request.estimated_hours ?? '')
  const [actualHours, setActualHours] = useState(request.actual_hours ?? '')
  const [estimatedCost, setEstimatedCost] = useState(request.estimated_cost ?? '')
  const [billingNotes, setBillingNotes] = useState(request.billing_notes ?? '')
  const [resolutionNotes, setResolutionNotes] = useState(request.resolution_notes ?? '')
  const [assignedTo, setAssignedTo] = useState(request.assigned_to ?? '')

  const { data: admins } = useAdmins()
  const updateRequest = useUpdateAssistanceRequest()

  const handleSave = async () => {
    try {
      await updateRequest.mutateAsync({
        id: request.id,
        updates: {
          status,
          is_covered: isCovered,
          estimated_hours: estimatedHours === '' ? null : Number(estimatedHours),
          actual_hours: actualHours === '' ? null : Number(actualHours),
          estimated_cost: estimatedCost === '' ? null : Number(estimatedCost),
          billing_notes: billingNotes || null,
          resolution_notes: resolutionNotes || null,
          assigned_to: assignedTo || null,
        },
      })
      toast.success('Demande mise à jour')
      onClose()
    } catch {
      toast.error('Erreur lors de la mise à jour')
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={request.title} size="lg">
      <div className="space-y-4">
        {/* Badge Assistance */}
        <div className="flex items-center gap-2">
          <Badge variant="purple" size="md">
            <Wrench className="w-3.5 h-3.5 mr-1" />
            Assistance
          </Badge>
          <Badge variant={ASSISTANCE_CATEGORY_LABELS[request.category] ? 'blue' : 'gray'} size="sm">
            {ASSISTANCE_CATEGORY_LABELS[request.category] || request.category}
          </Badge>
          <Badge variant={PRIORITY_CONFIG[request.priority]?.color} size="sm">
            {PRIORITY_CONFIG[request.priority]?.label}
          </Badge>
        </div>

        {/* Description */}
        <div>
          <label className="text-xs font-medium text-brown-500 uppercase">Description</label>
          <p className="mt-1 text-sm text-brown-700 whitespace-pre-wrap">{request.description}</p>
        </div>

        {/* Client & Projet */}
        <div className="grid grid-cols-2 gap-4 pt-2 border-t border-beige-200">
          <div>
            <label className="text-xs font-medium text-brown-500 uppercase">Client</label>
            <p className="mt-1 text-sm text-brown-700">
              {request.user?.full_name || request.user?.email || '-'}
            </p>
          </div>
          <div>
            <label className="text-xs font-medium text-brown-500 uppercase">Projet</label>
            {request.project ? (
              <Link
                to={`/projects/${request.project.id}`}
                className="mt-1 block text-sm text-mint-600 hover:text-mint-700"
              >
                {request.project.title}
              </Link>
            ) : (
              <p className="mt-1 text-sm text-brown-500">-</p>
            )}
          </div>
        </div>

        {/* Statut */}
        <div>
          <label className="block text-sm font-medium text-brown-700 mb-1">Statut</label>
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value as AssistanceStatus)}
          >
            {Object.entries(ASSISTANCE_STATUS_LABELS).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </Select>
        </div>

        {/* Couverture & Facturation */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-brown-700 mb-1">Couvert par le contrat</label>
            <Select
              value={isCovered === null ? '' : isCovered ? 'true' : 'false'}
              onChange={(e) => setIsCovered(e.target.value === '' ? null : e.target.value === 'true')}
            >
              <option value="">Non déterminé</option>
              <option value="true">Oui - Couvert</option>
              <option value="false">Non - Facturable</option>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-brown-700 mb-1">Admin assigné</label>
            <Select value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)}>
              <option value="">Non assigné</option>
              {admins?.map((admin) => (
                <option key={admin.id} value={admin.id}>
                  {admin.full_name}
                </option>
              ))}
            </Select>
          </div>
        </div>

        {/* Heures & Coût */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-brown-700 mb-1">Heures estimées</label>
            <input
              type="number"
              step="0.5"
              min="0"
              value={estimatedHours}
              onChange={(e) => setEstimatedHours(e.target.value === '' ? '' : Number(e.target.value))}
              className="w-full px-3 py-2 border border-beige-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-mint-500 text-sm"
              placeholder="0"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-brown-700 mb-1">Heures réelles</label>
            <input
              type="number"
              step="0.5"
              min="0"
              value={actualHours}
              onChange={(e) => setActualHours(e.target.value === '' ? '' : Number(e.target.value))}
              className="w-full px-3 py-2 border border-beige-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-mint-500 text-sm"
              placeholder="0"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-brown-700 mb-1">Coût estimé (€)</label>
            <input
              type="number"
              step="1"
              min="0"
              value={estimatedCost}
              onChange={(e) => setEstimatedCost(e.target.value === '' ? '' : Number(e.target.value))}
              className="w-full px-3 py-2 border border-beige-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-mint-500 text-sm"
              placeholder="0"
            />
          </div>
        </div>

        {/* Notes facturation */}
        <div>
          <label className="block text-sm font-medium text-brown-700 mb-1">Notes de facturation</label>
          <textarea
            value={billingNotes}
            onChange={(e) => setBillingNotes(e.target.value)}
            placeholder="Détails de facturation..."
            rows={2}
            className="w-full px-3 py-2 border border-beige-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-mint-500 text-sm"
          />
        </div>

        {/* Notes résolution */}
        <div>
          <label className="block text-sm font-medium text-brown-700 mb-1">Notes de résolution</label>
          <textarea
            value={resolutionNotes}
            onChange={(e) => setResolutionNotes(e.target.value)}
            placeholder="Détails de la résolution..."
            rows={2}
            className="w-full px-3 py-2 border border-beige-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-mint-500 text-sm"
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-beige-200">
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={handleSave} disabled={updateRequest.isPending}>
            {updateRequest.isPending ? 'Enregistrement...' : 'Sauvegarder'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default function AssistanceRequestsPage() {
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<AssistanceStatus | ''>('')
  const [priorityFilter, setPriorityFilter] = useState<AssistancePriority | ''>('')
  const [coverageFilter, setCoverageFilter] = useState<string>('')
  const [page, setPage] = useState(1)
  const [selectedRequest, setSelectedRequest] = useState<AssistanceRequestWithDetails | null>(null)

  const debouncedSearch = useDebounce(search, 300)

  const { data, isLoading } = useAssistanceRequests({
    search: debouncedSearch || undefined,
    status: statusFilter || undefined,
    priority: priorityFilter || undefined,
    isCovered: coverageFilter || undefined,
    page,
    limit: ITEMS_PER_PAGE,
  })

  const { data: stats } = useAssistanceStats()

  const totalPages = Math.ceil((data?.total || 0) / ITEMS_PER_PAGE)

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-display font-bold text-brown-900">
          Demandes d'assistance
        </h1>
        <p className="mt-1 text-brown-600">
          {data?.total || 0} demande{(data?.total || 0) > 1 ? 's' : ''}
        </p>
      </div>

      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card padding="sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-brown-600">Total</p>
                <p className="text-2xl font-bold text-brown-900">{stats.total}</p>
              </div>
              <Wrench className="w-8 h-8 text-brown-400" />
            </div>
          </Card>
          <Card padding="sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-brown-600">Soumises</p>
                <p className="text-2xl font-bold text-purple-600">{stats.submitted}</p>
              </div>
              <Clock className="w-8 h-8 text-purple-400" />
            </div>
          </Card>
          <Card padding="sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-brown-600">En cours</p>
                <p className="text-2xl font-bold text-gold-600">{stats.in_progress}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-gold-500" />
            </div>
          </Card>
          <Card padding="sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-brown-600">Facturables</p>
                <p className="text-2xl font-bold text-red-600">{stats.billable}</p>
              </div>
              <CreditCard className="w-8 h-8 text-red-400" />
            </div>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card padding="sm">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 min-w-[200px]">
            <Input
              type="search"
              placeholder="Rechercher une demande..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(1)
              }}
              leftIcon={<Search className="w-4 h-4" />}
            />
          </div>
          <div className="w-full sm:w-44">
            <Select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as AssistanceStatus | '')
                setPage(1)
              }}
            >
              <option value="">Tous les statuts</option>
              {Object.entries(ASSISTANCE_STATUS_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </Select>
          </div>
          <div className="w-full sm:w-40">
            <Select
              value={priorityFilter}
              onChange={(e) => {
                setPriorityFilter(e.target.value as AssistancePriority | '')
                setPage(1)
              }}
            >
              <option value="">Toutes priorités</option>
              {Object.entries(PRIORITY_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </Select>
          </div>
          <div className="w-full sm:w-40">
            <Select
              value={coverageFilter}
              onChange={(e) => {
                setCoverageFilter(e.target.value)
                setPage(1)
              }}
            >
              <option value="">Couverture</option>
              <option value="true">Couvert</option>
              <option value="false">Non couvert</option>
            </Select>
          </div>
        </div>
      </Card>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : !data?.requests.length ? (
        <EmptyState
          icon={<Wrench className="w-12 h-12" />}
          title="Aucune demande"
          description="Il n'y a aucune demande d'assistance pour le moment."
        />
      ) : (
        <>
          <Card padding="none">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-brown-200">
                    <th className="px-6 py-3 text-left text-xs font-medium text-brown-600 uppercase tracking-wider">
                      Titre
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-brown-600 uppercase tracking-wider">
                      Catégorie
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-brown-600 uppercase tracking-wider">
                      Priorité
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-brown-600 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-brown-600 uppercase tracking-wider">
                      Couverture
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-brown-600 uppercase tracking-wider">
                      Projet
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-brown-600 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-brown-600 uppercase tracking-wider">
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-brown-200">
                  {data.requests.map((req) => (
                    <tr
                      key={req.id}
                      className="hover:bg-brown-50 transition-colors cursor-pointer"
                      onClick={() => setSelectedRequest(req)}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-start gap-2">
                          <Wrench className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-brown-900">{req.title}</p>
                            <p className="text-sm text-brown-500 line-clamp-1">{req.description}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-brown-700">
                        {ASSISTANCE_CATEGORY_LABELS[req.category] || req.category}
                      </td>
                      <td className="px-6 py-4">
                        <Badge variant={PRIORITY_CONFIG[req.priority]?.color} size="sm">
                          {PRIORITY_CONFIG[req.priority]?.label}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        <Badge variant={ASSISTANCE_STATUS_CONFIG[req.status]?.color} size="sm">
                          {ASSISTANCE_STATUS_CONFIG[req.status]?.label}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        {req.is_covered === true && (
                          <Badge variant="mint" size="sm">Couvert</Badge>
                        )}
                        {req.is_covered === false && (
                          <Badge variant="gold" size="sm">Facturable</Badge>
                        )}
                        {req.is_covered === null && (
                          <span className="text-sm text-brown-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {req.project ? (
                          <Link
                            to={`/projects/${req.project.id}`}
                            className="text-sm text-mint-600 hover:text-mint-700"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {req.project.title}
                          </Link>
                        ) : (
                          <span className="text-sm text-brown-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-brown-700">
                        {req.user?.full_name || req.user?.email || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-brown-600">
                        {formatDistanceToNow(new Date(req.created_at), {
                          addSuffix: true,
                          locale: fr,
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {totalPages > 1 && (
            <div className="flex justify-center">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </div>
          )}
        </>
      )}

      {/* Detail modal */}
      {selectedRequest && (
        <RequestDetailModal
          key={selectedRequest.id}
          request={selectedRequest}
          isOpen={!!selectedRequest}
          onClose={() => setSelectedRequest(null)}
        />
      )}
    </div>
  )
}
